import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, CalendarClock, Plus, Trash2, Target, Brain, ClipboardList, Loader2 } from "lucide-react";

const Progress = () => {
  const [prog, setProg] = useState(null);
  const [exams, setExams] = useState([]);
  const [plan, setPlan] = useState(null);
  const [form, setForm] = useState({ subject: "", date: "", level: "GCSE" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, e, pl] = await Promise.allSettled([api.get("/progress"), api.get("/exams"), api.get("/plan")]);
    if (p.status === "fulfilled") setProg(p.value.data);
    if (e.status === "fulfilled") setExams(e.value.data || []);
    if (pl.status === "fulfilled") setPlan(pl.value.data);
  };
  useEffect(() => { load(); }, []);

  const addExam = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      await api.post("/exams", form);
      toast.success("Exam added");
      setForm({ subject: "", date: "", level: form.level });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const delExam = async (id) => { await api.delete(`/exams/${id}`); load(); };

  if (!prog) return <div className="p-10 text-neutral-500 text-[0.86rem]">Loading…</div>;

  const subjects = prog.subjects || [];

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="progress-page">
      <header className="mb-10 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono mb-3">/ progress</div>
          <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">Where you&apos;d land today.</h1>
          <p className="text-neutral-500 mt-3 text-[0.92rem] max-w-2xl">
            Predicted from your real practice scores, how well you&apos;re retaining cards, and how much of the
            specification has actually been taught.
          </p>
        </div>
        {prog.next_exam && (
          <div className="card-panel px-5 py-3.5" data-testid="next-exam-tile">
            <div className="label-mono text-[0.6rem] mb-1 flex items-center gap-1.5"><CalendarClock size={11}/> next exam</div>
            <div className="font-display text-2xl tightest font-semibold tabular leading-none">
              {prog.next_exam.days_left}<span className="text-sm text-neutral-500 ml-1.5">days</span>
            </div>
            <div className="text-[0.72rem] text-neutral-500 mt-1">{prog.next_exam.subject}</div>
          </div>
        )}
      </header>

      <section className="grid md:grid-cols-2 gap-3 mb-10">
        {subjects.length === 0 && (
          <div className="md:col-span-2 text-neutral-500 text-[0.86rem] border border-dashed border-white/[0.08] p-6 text-center rounded-[2px]">
            Record a lesson, generate a practice test and take it — your predicted grades appear here.
          </div>
        )}
        {subjects.map((s) => (
          <div key={s.subject} className="glass p-6" data-testid={`progress-${s.subject}`}>
            <div className="flex items-start justify-between gap-4 mb-5">
              <div className="min-w-0">
                <div className="label-mono mb-2">{s.level || "subject"}</div>
                <div className="font-display text-xl tightest font-semibold truncate">{s.subject}</div>
                {s.days_left !== null && s.days_left !== undefined && (
                  <div className="text-[0.75rem] text-amber-400 mt-1.5 tabular">
                    {s.days_left >= 0 ? `${s.days_left} days to exam` : "exam passed"}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-4xl tightest font-semibold leading-none" data-testid={`grade-${s.subject}`}>
                  {s.predicted_grade || "—"}
                </div>
                <div className="text-[0.68rem] text-neutral-500 mt-1.5 uppercase tracking-[0.14em] font-mono">
                  {s.predicted_grade ? `${s.confidence} confidence` : "no data"}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4">
              <Metric icon={ClipboardList} label="tests" value={s.recent_pct !== null ? `${s.recent_pct}%` : "—"} sub={`${s.attempts} taken`} />
              <Metric icon={Brain} label="retention" value={s.retention_pct !== null ? `${s.retention_pct}%` : "—"} sub={`${s.cards} cards`} />
              <Metric icon={Target} label="spec" value={s.coverage_pct !== null ? `${s.coverage_pct}%` : "—"} sub="covered" />
            </div>

            {s.score_pct !== undefined && s.score_pct !== null && (
              <div className="h-1 bg-white/[0.05] rounded-[1px] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-500 to-rose-500" style={{ width: `${s.score_pct}%` }} />
              </div>
            )}
            {s.trend !== 0 && s.recent_pct !== null && (
              <div className={`mt-3 text-[0.75rem] flex items-center gap-1.5 ${s.trend > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {s.trend > 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>}
                {s.trend > 0 ? "+" : ""}{s.trend}% vs your average
              </div>
            )}
          </div>
        ))}
      </section>

      <section className="grid md:grid-cols-3 gap-4 mb-10">
        <div className="glass p-6">
          <div className="label-mono mb-4 flex items-center gap-2"><CalendarClock size={11}/> exam dates</div>
          <form onSubmit={addExam} className="space-y-2 mb-5">
            <input className="input-cyber" placeholder="Subject (e.g. Physics)" value={form.subject} onChange={(e)=>setForm({...form, subject:e.target.value})} required data-testid="exam-subject"/>
            <input className="input-cyber" type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} required data-testid="exam-date"/>
            <select className="input-cyber" value={form.level} onChange={(e)=>setForm({...form, level:e.target.value})} data-testid="exam-level">
              {["GCSE", "IGCSE", "AS-Level", "A-Level", "IB Diploma"].map((l) => <option key={l} value={l} className="bg-black">{l}</option>)}
            </select>
            <button className="btn-primary w-full" disabled={busy} data-testid="exam-add">
              {busy ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Add exam
            </button>
          </form>
          <div className="space-y-1.5">
            {exams.length === 0 && <div className="text-neutral-500 text-[0.82rem]">No exams added.</div>}
            {exams.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/[0.04] last:border-0" data-testid={`exam-${e.id}`}>
                <div className="min-w-0">
                  <div className="text-[0.84rem] text-white truncate">{e.subject}</div>
                  <div className="text-[0.7rem] text-neutral-500 tabular">{e.date} · {e.days_left}d</div>
                </div>
                <button onClick={() => delExam(e.id)} className="text-neutral-600 hover:text-rose-400 transition-colors p-1" data-testid={`exam-del-${e.id}`}>
                  <Trash2 size={12}/>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="glass p-6 md:col-span-2">
          <div className="label-mono mb-4">/ your plan</div>
          {!plan?.days?.length && <div className="text-neutral-500 text-[0.86rem]">{plan?.note || "Plan appears once you have lessons and exam dates."}</div>}
          <div className="space-y-2 max-h-[26rem] overflow-y-auto pr-1" data-testid="plan-list">
            {plan?.days?.map((d) => (
              <div key={d.date} className="border border-white/[0.06] rounded-[2px] p-3.5" data-testid={`plan-${d.date}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[0.82rem] text-white">
                    {d.day_offset === 0 ? "Today" : new Date(d.date).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                  </div>
                  <div className="label-mono text-[0.62rem]">{d.focus}</div>
                </div>
                {d.exams?.length > 0 && (
                  <div className="text-[0.75rem] text-rose-400 mb-1.5">EXAM: {d.exams.join(", ")}</div>
                )}
                <ul className="space-y-1">
                  {d.tasks.map((t, i) => (
                    <li key={i} className="text-[0.8rem] text-neutral-400 flex items-start gap-2">
                      <span className="text-neutral-600 mt-0.5">·</span>{t.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, sub }) => (
  <div>
    <div className="label-mono mb-1.5 flex items-center gap-1.5 text-[0.6rem]"><Icon size={10}/> {label}</div>
    <div className="text-[0.95rem] text-white tabular">{value}</div>
    <div className="text-[0.68rem] text-neutral-600">{sub}</div>
  </div>
);

export default Progress;
